import { useState } from "react";

import { useTranslation } from "next-i18next";

import { ChevronDown } from "@/assets/icons/24/ChevronDown";
import { cn } from "@/common/utils/css.utils";
import { ListingMarkdown } from "@/components/search/plp-seo/ListingMarkdown";
import type { ListingQuestion } from "@/components/search/plp-seo/listing-description.utils";
import { PLP_SEO_KEYS } from "@/components/search/plp-seo/plp-seo.i18n";
import {
  PLP_SEO_LEAF_HEADING_SIZE,
  PLP_SEO_SECTION_HEADING_SIZE,
  PLP_SEO_TEXT_WIDTH_CLASS,
} from "@/components/search/plp-seo/plp-seo.tokens";
import { Heading } from "@/ui/cva/Heading";

/**
 * The buyer questions from the page copy as an accordion (WHO-5534).
 *
 * Native `details`/`summary`, so the answers are in the HTML whether or not the row is open
 * and no script is needed to read them. The FAQPage JSON-LD for these questions is emitted
 * by `SearchSeo` from the same headings, so markup and visible answers
 * always agree.
 *
 * Every row is closed by default on every viewport (review round 2: desktop too, the block
 * stays compact and Google still reads the answers). One row open at a time. Rows are 44px
 * tall at minimum. No bottom border: the section separator follows directly (Vlad, 09-23).
 */
export const FaqAccordion = ({
  questions,
}: {
  questions: ListingQuestion[];
}) => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  if (questions.length === 0) {
    return null;
  }

  return (
    <div className={PLP_SEO_TEXT_WIDTH_CLASS}>
      <Heading as="h3" size={PLP_SEO_SECTION_HEADING_SIZE} className="mb-3">
        {t(PLP_SEO_KEYS.faqHeading, "Frequently asked questions")}
      </Heading>
      <div className="divide-y divide-grey-300 border-t border-grey-300">
        {questions.map((item, index) => {
          const open = openIndex === index;

          return (
            <details
              key={item.question}
              open={open}
              onToggle={event => {
                /* Read before the updater runs: React nulls `currentTarget` after dispatch. */
                const isOpen = event.currentTarget.open;

                setOpenIndex(current =>
                  isOpen ? index : current === index ? null : current
                );
              }}
              className="group"
            >
              <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-4 py-2.5 [&::-webkit-details-marker]:hidden">
                <Heading
                  as="h4"
                  size={PLP_SEO_LEAF_HEADING_SIZE}
                  className="text-grey-800"
                >
                  {item.question}
                </Heading>
                <ChevronDown
                  aria-hidden
                  className={cn(
                    "size-5 shrink-0 text-grey-600 transition-transform",
                    open && "rotate-180"
                  )}
                />
              </summary>
              <div className="pb-3">
                <ListingMarkdown markdown={item.answerMarkdown} />
              </div>
            </details>
          );
        })}
      </div>
    </div>
  );
};
